import React, { useEffect, useRef, useState } from "react";
import { Input, Label } from "@src/common/components";

export const MIN_INTERVAL_WEEKS = 2;
export const MAX_INTERVAL_WEEKS = 52;
export const DEFAULT_INTERVAL_WEEKS = 3;

/**
 * Coerces raw input text to a valid interval. Blank or non-numeric input falls
 * back to the default rather than erroring: the field is constrained so that
 * form state can never hold a value cube would reject.
 */
export function clampIntervalWeeks(raw: string): number {
  const parsed: number = parseInt(raw, 10);
  if (Number.isNaN(parsed)) return DEFAULT_INTERVAL_WEEKS;
  if (parsed < MIN_INTERVAL_WEEKS) return MIN_INTERVAL_WEEKS;
  if (parsed > MAX_INTERVAL_WEEKS) return MAX_INTERVAL_WEEKS;
  return parsed;
}

interface Props {
  intervalWeeks: number;
  setIntervalWeeks: (weeks: number) => void;
}

function EveryNWeeksIntervalInput({ intervalWeeks, setIntervalWeeks }: Props) {
  // The raw string is held locally so a partially-typed value survives until
  // blur. Clamping on every keystroke would turn "12" into "2" at the first digit.
  const [rawValue, setRawValue] = useState<string>(String(intervalWeeks));
  // Tracks whether the user is actively editing the field. While true, the
  // resync effect below must not overwrite the display: handleChange now
  // commits the clamped value on every keystroke (not just in-range ones),
  // and without this guard the effect would see the prop change and rewrite
  // "1" to "2" mid-keystroke -- the exact keystroke-clamping this component
  // is designed to avoid.
  const isEditingRef = useRef<boolean>(false);

  useEffect(() => {
    // Only resync from the prop when it genuinely disagrees with what's
    // typed, and never while the user is mid-edit (see isEditingRef above).
    if (isEditingRef.current) return;
    if (parseInt(rawValue, 10) !== intervalWeeks) {
      setRawValue(String(intervalWeeks));
    }
  }, [intervalWeeks]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    isEditingRef.current = true;
    const raw: string = event.target.value;
    setRawValue(raw);
    // Always commit the clamped value, even when the typed text is blank or
    // out of range: implicit form submission via Enter does not blur the
    // focused field, so anything short of "always commit clampIntervalWeeks"
    // lets the parent submit a stale or out-of-range-prefix interval. See
    // EveryNWeeksIntervalInput.test.tsx for the regression this guards.
    setIntervalWeeks(clampIntervalWeeks(raw));
  };

  const handleBlur = () => {
    isEditingRef.current = false;
    const clamped: number = clampIntervalWeeks(rawValue);
    setRawValue(String(clamped));
    setIntervalWeeks(clamped);
  };

  return (
    <div className="flex flex-col gap-2">
      <Label className="truncate text-xs dark:text-gray-300">
        Repeat every
      </Label>
      <div className="flex items-center gap-2">
        <div className="w-20">
          <Input
            type="number"
            size="mini"
            min={MIN_INTERVAL_WEEKS}
            max={MAX_INTERVAL_WEEKS}
            value={rawValue}
            aria-label="Repeat every N weeks"
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
        <span className="text-xs text-gray-700 dark:text-gray-300">weeks</span>
      </div>
    </div>
  );
}

export default EveryNWeeksIntervalInput;
